import { useCallback, useMemo } from 'react';
import {
    GENERIC_ERROR_MESSAGE,
    MIN_API_RESPONSE_TIME,
    NETWORK_ERROR_MESSAGE,
    PageActionBindingType,
} from '../constants';
import type { FlowStore, Target } from '../services/flowReducer';
import { invoke, sync } from '../services/network';
import { isValid, validateComponent } from '../services/validation';
import { type AppState, PageType } from '../types/models';
import type {
    Component,
    Container,
    ExecuteParameters,
    FireEventParameters,
    ObjectData,
    ObjectDataProperty,
    PageElement,
} from '../types/public/models';
import { isNetworkError, reportError } from '../utils/errors';
import { getElementById, isComponent, updateElementTree } from '../utils/state';

/**
 * Walks a page element tree revalidating every component, returning the
 * (possibly updated) tree along with the ids of any invalid components.
 * Ported verbatim from the previous appState.ts.
 */
const checkPageElementIsValid = (
    element: PageElement,
): { element: PageElement; invalidIds: string[] } => {
    if (!isComponent(element)) {
        const invalidIds: string[] = [];
        const children = element.children.map((child) => {
            const { element, invalidIds: childInvalidIds } = checkPageElementIsValid(child);
            invalidIds.push(...childInvalidIds);
            return element;
        });
        return {
            element: { ...element, children },
            invalidIds,
        };
    }

    const { isValid, validationMessage } = validateComponent(element);
    const invalidIds = isValid ? [] : [element.id];

    if (element.isValid !== isValid || element.validationMessage !== validationMessage) {
        return {
            element: { ...element, isValid, validationMessage },
            invalidIds,
        };
    }

    return { element, invalidIds };
};

/**
 * Adds newly selected items (and any new objectData) to a component.
 */
const updateSelectedItems = (component: Component, items: ObjectData[]): Component => {
    const newSelectedItems: ObjectData[] = [...(component.selectedItems || [])];
    const newObjectData: ObjectData[] = [...(component.objectData || [])];

    items.forEach((item) => {
        const itemExistsInObjectData = newObjectData.some(
            (od) => od.externalId === item.externalId && od.internalId === item.internalId,
        );

        if (!itemExistsInObjectData) {
            newObjectData.push(item);
        }
    });

    if (component.isMultiSelect) {
        items.forEach((item) => {
            const itemIsSelected = newSelectedItems.some(
                (selectedItem) =>
                    selectedItem.externalId === item.externalId &&
                    selectedItem.internalId === item.internalId,
            );
            if (!itemIsSelected) {
                newSelectedItems.push(item);
            }
        });
    } else if (items[0].externalId !== component.selectedItems?.[0]?.externalId) {
        newSelectedItems.push(items[0]);
    }

    return { ...component, selectedItems: newSelectedItems, objectData: newObjectData };
};

/** Maps a thrown error to the message that should be shown to the user. */
const toErrorMessage = (error: unknown): string => {
    if (error instanceof Error) {
        // No status code means the network request failed (offline).
        if (isNetworkError(error)) {
            return NETWORK_ERROR_MESSAGE;
        }
        // A status code means an error message was supplied in the api response.
        reportError(error.message);
    }
    return GENERIC_ERROR_MESSAGE;
};

export interface FlowActions {
    execute: (params: ExecuteParameters) => Promise<void>;
    updateElement: (params: FireEventParameters) => void;
    updateObjectData: (params: {
        elementId: string;
        objectData: ObjectData[];
        fn?: (
            existingProperty: ObjectDataProperty,
            newProperty: ObjectDataProperty,
        ) => ObjectDataProperty;
    }) => void;
    selectItems: (params: {
        elementId: string;
        items: ObjectData[];
        triggersPageCondition?: boolean | undefined;
    }) => Promise<void>;
    setErrorMessage: (message: string | null) => void;
    setAppState: (appState: AppState) => void;
}

/**
 * Builds the set of state operations for a given target ('base' or 'modal').
 */
export function useFlowActions(target: Target, store: FlowStore): FlowActions {
    const { dispatch } = store;
    const getState = useCallback((): AppState => store.getState()[target], [store, target]);

    // only show the loader if the request takes longer than MIN_API_RESPONSE_TIME.
    const runWithLoader = useCallback(
        async (task: () => Promise<void>): Promise<void> => {
            let isWaiting = true;
            const timer = setTimeout(() => {
                if (isWaiting) {
                    dispatch({ type: 'SET_LOADING', target });
                }
            }, MIN_API_RESPONSE_TIME);
            try {
                await task();
            } finally {
                isWaiting = false;
                clearTimeout(timer);
            }
        },
        [dispatch, target],
    );

    const setErrorMessage = useCallback(
        (message: string | null) => dispatch({ type: 'SET_ERROR', message }),
        [dispatch],
    );

    const setAppState = useCallback(
        (appState: AppState) =>
            dispatch({
                type: 'SET_STATE',
                target: appState.pageType === PageType.Modal ? 'modal' : 'base',
                appState: { ...appState, isLoading: false },
            }),
        [dispatch],
    );

    const executePageCondition = useCallback(
        (elementId: string, elementPartial: Partial<PageElement>) =>
            runWithLoader(async () => {
                const state = getState();
                const updatedAppState = {
                    ...state,
                    rootElement: updateElementTree(elementId, elementPartial, state.rootElement),
                };

                try {
                    dispatch({
                        type: 'APPLY_CONDITION_RESULT',
                        result: await sync(updatedAppState),
                    });
                } catch (error) {
                    dispatch({ type: 'SET_ERROR', message: toErrorMessage(error) });
                }
            }),
        [dispatch, getState, runWithLoader],
    );

    const updateElement = useCallback(
        ({ elementId, elementPartial, triggersPageCondition = false }: FireEventParameters) => {
            const component = getElementById(getState().rootElement as PageElement, elementId);
            if (
                component &&
                isComponent(component) &&
                component.hasEvents &&
                triggersPageCondition
            ) {
                void executePageCondition(elementId, elementPartial);
            } else {
                dispatch({ type: 'UPDATE_ELEMENT', target, elementId, elementPartial });
            }
        },
        [dispatch, target, getState, executePageCondition],
    );

    const updateObjectData = useCallback<FlowActions['updateObjectData']>(
        ({ elementId, objectData, fn }) =>
            dispatch({ type: 'UPDATE_OBJECT_DATA', target, elementId, objectData, fn }),
        [dispatch, target],
    );

    const selectItems = useCallback(
        async ({
            elementId,
            items,
            triggersPageCondition = false,
        }: {
            elementId: string;
            items: ObjectData[];
            triggersPageCondition?: boolean | undefined;
        }) => {
            if (items.length === 0) {
                // Nothing to select (e.g. every file upload failed) - no-op.
                return;
            }

            const state = getState();
            const component = state.rootElement && getElementById(state.rootElement, elementId);

            if (!component || !isComponent(component)) {
                dispatch({ type: 'SET_ERROR', message: GENERIC_ERROR_MESSAGE });
                return;
            }

            const updatedComponent = updateSelectedItems(component, items);

            if (updatedComponent.hasEvents && triggersPageCondition) {
                await executePageCondition(elementId, updatedComponent);
            } else {
                dispatch({
                    type: 'UPDATE_ELEMENT',
                    target,
                    elementId,
                    elementPartial: updatedComponent,
                });
            }
        },
        [dispatch, target, getState, executePageCondition],
    );

    const execute = useCallback(
        ({
            outcomeId,
            invokeType = 'FORWARD',
            objectDataId,
            navigationElementId,
            selectedStateEntryId,
            selectedNavigationItemId,
            objectData,
        }: ExecuteParameters) =>
            runWithLoader(async () => {
                const state = getState();

                const isNavigating = Boolean(selectedNavigationItemId || selectedStateEntryId);

                let root = state.rootElement;
                const executedOutcome = (state.outcomes ?? []).find(
                    (outcome) => outcome.id === outcomeId,
                );

                if (objectData && executedOutcome?.pageObjectBindingId) {
                    root = updateElementTree(
                        executedOutcome.pageObjectBindingId,
                        { objectData: [objectData], selectedItems: [objectData] },
                        state.rootElement,
                    ) as Container;
                }

                if (
                    objectDataId !== undefined &&
                    state.rootElement &&
                    executedOutcome &&
                    executedOutcome.pageObjectBindingId !== null
                ) {
                    // An objectDataId means the invoke is in the context of a single
                    // objectData item (e.g. an action on a table row), so the request
                    // must contain only that single item for the bound component.
                    const component = getElementById(
                        state.rootElement,
                        executedOutcome.pageObjectBindingId,
                    );

                    if (component && isComponent(component)) {
                        const selectedItems = (component.objectData ?? []).filter(
                            (od) => od.externalId === objectDataId,
                        );

                        root = updateElementTree(
                            component.id,
                            { ...component, selectedItems } as Partial<Component>,
                            state.rootElement,
                        );
                    }
                }

                const stateAfterBulkSelectionsApplied: AppState = {
                    ...state,
                    rootElement: root,
                    hasSubmitted: true,
                };

                const invalidPageElementIds: string[] = [];

                const validatedAppState: AppState =
                    executedOutcome?.pageActionBindingType === PageActionBindingType.Save &&
                    stateAfterBulkSelectionsApplied.rootElement !== null
                        ? {
                              ...stateAfterBulkSelectionsApplied,
                              rootElement: {
                                  ...stateAfterBulkSelectionsApplied.rootElement,
                                  children:
                                      stateAfterBulkSelectionsApplied.rootElement.children.map(
                                          (child) => {
                                              const { element, invalidIds } =
                                                  checkPageElementIsValid(child);
                                              invalidPageElementIds.push(...invalidIds);
                                              return element;
                                          },
                                      ),
                              },
                          }
                        : stateAfterBulkSelectionsApplied;

                const stateIsValid =
                    validatedAppState.rootElement && isValid(validatedAppState.rootElement);
                const isSaveAction =
                    executedOutcome?.pageActionBindingType === PageActionBindingType.Save;

                if (isSaveAction && !isNavigating && !stateIsValid) {
                    // Bail out early if the state isn't valid. Validation does not apply
                    // when navigating and only applies for Save outcomes. The components
                    // will re-render their invalid state.
                    dispatch({ type: 'APPLY_MOVE_RESULT', result: validatedAppState });
                    document
                        .querySelector(`[id='${invalidPageElementIds.join("'], [id='")}']`)
                        ?.closest('.page-component')
                        ?.scrollIntoView();
                    return;
                }

                try {
                    const newState = await invoke({
                        appState: validatedAppState,
                        invokeType,
                        navigationElementId,
                        selectedNavigationItemId,
                        selectedStateEntryId,
                        selectedOutcomeId: executedOutcome?.id,
                    });

                    dispatch({ type: 'APPLY_MOVE_RESULT', result: newState });
                } catch (error) {
                    dispatch({ type: 'SET_ERROR', message: toErrorMessage(error) });
                }
            }),
        [dispatch, getState, runWithLoader],
    );

    return useMemo(
        () => ({
            execute,
            updateElement,
            updateObjectData,
            selectItems,
            setErrorMessage,
            setAppState,
        }),
        [execute, updateElement, updateObjectData, selectItems, setErrorMessage, setAppState],
    );
}
